import { Injectable } from '@angular/core';
import { FirebaseX, MessagePayload } from '@awesome-cordova-plugins/firebase-x/ngx';
import { Subscription } from 'rxjs';
import { Nullable } from 'src/core/common/domain/types/types';
import { PlatformService } from 'src/core/common/domain/platform/platform.service';
import { SessionService } from 'src/core/session/domain/session.service';
import { DeviceService } from 'src/core/device/device.service';
import { DeviceRepository } from 'src/core/device/domain/device-repository';
import { FcmDataPayload, parsePushNotification, PushNotificationSources } from './push-notification';
import { PushNotificationHandlerService } from './push-notification-handler.service';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private onMessageReceived$: Nullable<Subscription> = null;
  private onTokenRefresh$: Nullable<Subscription> = null;

  constructor(
    private readonly firebaseX: FirebaseX,
    private readonly platformService: PlatformService,
    private readonly sessionService: SessionService,
    private readonly deviceService: DeviceService,
    private readonly deviceRepository: DeviceRepository,
    private readonly handler: PushNotificationHandlerService
  ) {}

  /**
   * Called on app bootstrap. Binds the FCM listeners and caches the current token so it can
   * be inspected (session.fcm_token) and reused. The backend registration happens on login.
   */
  async init(): Promise<void> {
    if (!this.platformService.isRealMobileDevice()) {
      return;
    }

    this.bindListeners();

    try {
      const token = await this.firebaseX.getToken();
      if (token) {
        await this.sessionService.saveFCMToken(token);
      }
    } catch (error) {
      console.error('[PushNotificationService] Error getting FCM token', error);
    }
  }

  /**
   * Dismiss the app's push notifications from the OS notification tray. Only works on a real
   * device (the FirebaseX plugin is unavailable in the browser), so it is a no-op otherwise.
   */
  async clearSystemNotifications(): Promise<void> {
    if (!this.platformService.isRealMobileDevice()) {
      return;
    }

    await this.firebaseX.clearAllNotifications();
  }

  /**
   * Called after login. Requests permission (iOS), gets the token and registers it in the
   * backend together with the device UUID.
   */
  async register(): Promise<void> {
    if (!this.platformService.isRealMobileDevice()) {
      return;
    }

    try {
      const granted = await this.ensurePermission();
      if (!granted) {
        return;
      }

      const token = await this.firebaseX.getToken();
      if (!token) {
        return;
      }

      await this.sessionService.saveFCMToken(token);
      this.bindListeners();
      await this.deviceRepository.add(token, this.deviceService.getUuid());
    } catch (error) {
      console.error('[PushNotificationService] Error registering device', error);
    }
  }

  /**
   * Called on logout. Removes the token from the backend and clears the cached token.
   */
  async unregister(): Promise<void> {
    const token = this.sessionService.getFCMToken();
    this.unbindListeners();

    if (!token) {
      return;
    }

    try {
      await this.deviceRepository.remove(token, this.deviceService.getUuid());
    } catch (error) {
      console.error('[PushNotificationService] Error unregistering device', error);
    }

    await this.sessionService.clearFCMToken();
  }

  async onMessageReceived(data: MessagePayload & FcmDataPayload): Promise<void> {
    const notification = parsePushNotification(data);
    const source = data?.tap ? PushNotificationSources.BACKGROUND : PushNotificationSources.FOREGROUND;
    await this.handler.handleNotification(notification, source);
  }

  private async ensurePermission(): Promise<boolean> {
    const hasPermission = await this.firebaseX.hasPermission();
    if (hasPermission) {
      return true;
    }

    // iOS shows the system prompt; Android always resolves to granted.
    const granted = await this.firebaseX.grantPermission();
    return Boolean(granted);
  }

  private bindListeners(): void {
    this.unbindListeners();

    this.onTokenRefresh$ = this.firebaseX.onTokenRefresh().subscribe(async (token: string) => {
      await this.sessionService.saveFCMToken(token);
      if (this.sessionService.isAuthenticated()) {
        await this.deviceRepository.add(token, this.deviceService.getUuid());
      }
    });

    this.onMessageReceived$ = this.firebaseX
      .onMessageReceived()
      .subscribe(async (data) => await this.onMessageReceived(data as MessagePayload & FcmDataPayload));
  }

  private unbindListeners(): void {
    this.onMessageReceived$?.unsubscribe();
    this.onMessageReceived$ = null;
    this.onTokenRefresh$?.unsubscribe();
    this.onTokenRefresh$ = null;
  }
}
